/**
 * Keep the Knowledge Archive camera framed on the whole graph — §2.10 #25.
 *
 * `MindspaceGraph` used to call the library's `zoomToFit` once, 400ms after
 * the node COUNT changed. Both halves of that were wrong:
 *
 * - **Too early.** The custom forces are applied after mount and reheat the
 *   simulation, which then spreads the nodes for another ~200 ticks. A fit at
 *   400ms framed a cluster that was about to double in size, so every frame
 *   ended zoomed to 140–340% on part of the graph.
 * - **Wrong key.** A search that swaps 12 concepts for 4 changes the count,
 *   but a filter that swaps 4 for 4 different ones does not, and never
 *   refitted at all.
 *
 * So the fit is keyed on the node SET (ids), follows the layout while it
 * settles, and lands once more when the engine stops. The camera maths —
 * labels included, capped zoom — is `lib/mindspace/graphViewport`.
 *
 * ## Motion
 *
 * While the layout settles the camera re-frames every few ticks with no
 * transition, which reads as the view easing out with the graph; the final fit
 * animates over 400ms. Under `prefers-reduced-motion` neither moves: the
 * in-flight re-framing is skipped and the final fit is instant.
 *
 * ## Hands off once the user takes the camera
 *
 * A wheel zoom or a node drag stops the automatic framing until the node set
 * next changes — a resize should not yank back a view someone chose. The zoom
 * badge's fit (`fit()`) always works and hands control back.
 *
 * ## What a test can see
 *
 * A canvas has no DOM, so the hook reports `framed` (the final fit has
 * landed) and `nodesInView` (how many nodes, labels included, are fully inside
 * the canvas under the transform the LIBRARY actually applied). The graph
 * publishes both as data attributes.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { MutableRefObject } from 'react';
import type { ForceGraphMethods } from 'react-force-graph-2d';
import {
  computeFitViewport,
  countNodesInView,
  labelWidthPx,
  type FitNode,
} from '@/lib/mindspace/graphViewport';

/** Re-frame every this many ticks while the layout settles. */
const SETTLE_FIT_EVERY_TICKS = 6;
const FINAL_FIT_MS = 400;

interface UseGraphAutoFitOptions {
  graphRef: MutableRefObject<ForceGraphMethods | undefined>;
  nodes: Array<FitNode & { id: string }>;
  width: number;
  height: number;
  /** Off for the frozen `layout='fixed'` seam, which pins its own viewport. */
  enabled: boolean;
  /** The scale labels show from, so the fit leaves room for them. */
  labelZoom?: number;
}

/** How many nodes the camera the library ACTUALLY applied shows in full. */
const measureInView = (
  graph: ForceGraphMethods,
  {
    nodes,
    width,
    height,
    labelZoom,
  }: { nodes: FitNode[]; width: number; height: number; labelZoom?: number }
) =>
  countNodesInView(nodes, {
    labelZoom,
    k: graph.zoom(),
    toScreen: (x, y) => graph.graph2ScreenCoords(x, y),
    width,
    height,
    labelWidthPx,
  });

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export function useGraphAutoFit({
  graphRef,
  nodes,
  width,
  height,
  enabled,
  labelZoom,
}: UseGraphAutoFitOptions) {
  const [framed, setFramed] = useState(false);
  const [nodesInView, setNodesInView] = useState<number | null>(null);
  const pending = useRef(true);
  const userMoved = useRef(false);
  const ticks = useRef(0);
  const settleTimer = useRef<number | undefined>(undefined);
  const latest = useRef({ nodes, width, height, labelZoom });
  latest.current = { nodes, width, height, labelZoom };

  const nodeKey = useMemo(() => nodes.map(n => n.id).join('|'), [nodes]);

  const apply = useCallback(
    (durationMs: number, final: boolean) => {
      const fg = graphRef.current;
      const {
        nodes: current,
        width: w,
        height: h,
        labelZoom: lz,
      } = latest.current;
      const fit = fg && computeFitViewport(current, w, h, labelWidthPx, lz);
      if (!fg || !fit) return false;
      fg.zoom(fit.k, durationMs);
      fg.centerAt(fit.x, fit.y, durationMs);
      if (!final) return true;
      window.clearTimeout(settleTimer.current);
      settleTimer.current = window.setTimeout(() => {
        const graph = graphRef.current;
        if (!graph) return;
        setNodesInView(measureInView(graph, latest.current));
        setFramed(true);
      }, durationMs + 50);
      return true;
    },
    [graphRef]
  );

  // A new node set restarts the whole cycle: frame it again once it settles.
  useEffect(() => {
    pending.current = true;
    userMoved.current = false;
    ticks.current = 0;
    setFramed(false);
    setNodesInView(null);
  }, [nodeKey]);

  useEffect(() => () => window.clearTimeout(settleTimer.current), []);

  // Positions are assigned by the layout and pinned, so there is nothing to
  // wait for: frame the node set as soon as the canvas has a size. The engine
  // callbacks below were written for a simulation that spread the nodes; with
  // every node pinned the engine stops before the canvas is measured, so a
  // fit that waited for it never landed. The first frame of a mount is
  // instant (there is no previous view to ease from); a later node set eases.
  const everFramed = useRef(false);
  useEffect(() => {
    if (!enabled || !pending.current || userMoved.current) return;
    if (width <= 0 || height <= 0) return;
    const frame = window.requestAnimationFrame(() => {
      if (!pending.current || userMoved.current) return;
      const ms =
        !everFramed.current || prefersReducedMotion() ? 0 : FINAL_FIT_MS;
      if (apply(ms, true)) {
        pending.current = false;
        everFramed.current = true;
      }
    });
    return () => window.cancelAnimationFrame(frame);
  }, [enabled, nodeKey, width, height, apply]);

  // Re-frame on resize (panel collapse, window resize) unless the user has
  // taken the camera. Instant: the box itself just moved.
  useEffect(() => {
    if (!enabled || pending.current || userMoved.current) return;
    apply(0, true);
  }, [enabled, width, height, apply]);

  const onEngineTick = useCallback(() => {
    if (!enabled || !pending.current || userMoved.current) return;
    ticks.current += 1;
    if (ticks.current % SETTLE_FIT_EVERY_TICKS !== 0) return;
    if (!prefersReducedMotion()) apply(0, false);
  }, [enabled, apply]);

  const onEngineStop = useCallback(() => {
    if (!enabled || !pending.current) return;
    if (userMoved.current) {
      pending.current = false;
      return;
    }
    if (apply(prefersReducedMotion() ? 0 : FINAL_FIT_MS, true)) {
      pending.current = false;
    }
  }, [enabled, apply]);

  /** The zoom badge: frame everything now, and resume automatic framing. */
  const fit = useCallback(() => {
    userMoved.current = false;
    apply(prefersReducedMotion() ? 0 : FINAL_FIT_MS, true);
  }, [apply]);

  /** The user took the camera (wheel zoom, node drag). */
  const release = useCallback(() => {
    userMoved.current = true;
  }, []);

  return { framed, nodesInView, onEngineTick, onEngineStop, fit, release };
}
